<template>
  <v-card outlined class="academy-public-event-card rounded-lg"
    :class="{ 'academy-public-event-card--past': deactivated }" :ripple="false" @click="$emit('click')">

    <div class="academy-public-event-card__hero">

      <div v-if="heroImageSrc" class="academy-public-event-card__hero-img-wrap">
        <ImageDisplay :src="heroImageSrc" />
      </div>
      <div v-else class="academy-public-event-card__hero-placeholder">
        <img class="academy-public-event-card__hero-placeholder-img" :src="placeholderPublicEventsSrc" alt="" />
      </div>
    </div>

    <v-card-text class="px-4 py-4 academy-public-event-card__body">
      <div class="font-weight-bold text-subtitle-1 plusIndigoDarken1--text pb-4 title-line">
        {{ event.title }}
      </div>

      <div class="d-flex align-center mt-3 mb-2">
        <v-icon size="18" color="plusGreyDarken1" class="flex-shrink-0">mdi-calendar-month-outline</v-icon>
        <span class="text-subtitle-2 ml-2 plusGreyDarken1--text">{{ shortDate }}</span>
      </div>
      <div class="d-flex align-center mb-2">
        <v-icon size="18" color="plusGreyDarken1" class="flex-shrink-0">mdi-clock-outline</v-icon>
        <span class="text-subtitle-2 ml-2 plusGreyDarken1--text">{{ timeRange }}</span>
      </div>
      <div v-if="event.participants.length > 0" class="d-flex align-center mb-2">
        <v-icon size="18" color="plusGreyDarken1" class="flex-shrink-0">mdi-account-multiple-outline</v-icon>
        <span class="text-subtitle-2 ml-2 plusGreyDarken1--text">{{ event.participants.length }} + more</span>
      </div>
      <div v-if="attendeesLine" class="d-flex align-center mb-2">
        <v-icon size="18" color="plusGreyDarken1" class="flex-shrink-0">mdi-account-outline</v-icon>
        <span class="text-subtitle-2 ml-2 plusGreyDarken1--text">{{ attendeesLine }}</span>
      </div>
      <div class="d-flex align-center">
        <v-icon size="18" color="plusGreyDarken1" class="flex-shrink-0">{{ priceIcon }}</v-icon>
        <span class="text-subtitle-2 ml-2 plusGreyDarken1--text">{{ priceLabel }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import ImageDisplay from "@/components/displays/ImageDisplay.vue";

export default {
  name: "AcademyPublicEventCard",
  components: { ImageDisplay },
  props: {
    event: {
      type: Object,
      required: true
    },
    deactivated: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    placeholderPublicEventsSrc() {
      return require("@/assets/placeholder_public_events.png");
    },
    heroImageSrc() {
      const e = this.event;
      if (e.image) return e.image;
      if (e.displayAcademyImage && this.$store.state.academyImage) {
        return this.$store.state.academyImage;
      }
      return null;
    },
    shortDate() {
      const d = this.event.date;
      if (!d) return "";
      const date = d instanceof Date ? d : new Date(d);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    },
    timeRange() {
      const e = this.event;
      if (!e.date) return "";
      const start = e.date instanceof Date ? e.date : new Date(e.date);
      const startStr = start.toLocaleTimeString(this.$i18n.locale, {
        hour: "numeric",
        minute: "2-digit"
      });
      if (!e.endDate) return startStr;
      const end = e.endDate instanceof Date ? e.endDate : new Date(e.endDate);
      const endStr = end.toLocaleTimeString(this.$i18n.locale, {
        hour: "numeric",
        minute: "2-digit"
      });
      return `${startStr} - ${endStr}`;
    },
    attendeesLine() {
      const e = this.event;
      const first =
        e.firstParticipantName ||
        e.firstParticipant ||
        e.firstParticipantDisplayName;
      const count =
        e.participantCount ?? e.totalParticipants ?? e.registeredCount ?? null;

      if (this.deactivated) {
        if (first) return first;
        if (count != null && count > 0) {
          return `${count} ${this.$t("message.participants")}`;
        }
        return null;
      }

      if (first && count != null && count > 1) {
        return `${first} + ${count - 1} ${this.$t("message.more")}`;
      }
      if (first) return first;
      if (count != null && count > 0) {
        return `${count} ${this.$t("message.participants")}`;
      }
      return null;
    },
    ticketAmount() {
      const t = this.event.ticket;
      if (!t || t.amountDecimal == null) return 0;
      const n = Number(t.amountDecimal);
      return Number.isFinite(n) ? n : 0;
    },
    isPaid() {
      return this.ticketAmount > 0;
    },
    priceIcon() {
      return this.isPaid ? "mdi-currency-usd" : "mdi-currency-usd-off";
    },
    priceLabel() {
      if (this.isPaid) {
        return this.$t("message.paid_event");
      }
      return this.$t("message.free_event");
    }
  }
};
</script>

<style lang="scss" scoped>
.academy-public-event-card {
  cursor: pointer;
  border-color: var(--v-plusBorder-base, #e0e0e0) !important;
  transition: background-color 0.15s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.academy-public-event-card:hover {
  background-color: var(--v-plusGreyLighten5-base, #fafafa);
}

.academy-public-event-card__body {
  flex-grow: 1;
}

.academy-public-event-card__hero {
  height: 160px;
  overflow: hidden;
  background: var(--v-plusSurface-base, #f5f5f5);
  border-radius: 8px 8px 0 0;
}

.academy-public-event-card--past .academy-public-event-card__hero-img-wrap ::v-deep img {
  filter: grayscale(1);
}

.academy-public-event-card__hero-placeholder {
  height: 100%;
  width: 100%;
}

.academy-public-event-card__hero-placeholder-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.academy-public-event-card--past .academy-public-event-card__hero-placeholder-img {
  filter: grayscale(1);
}

.academy-public-event-card__hero-img-wrap {
  height: 100%;
  width: 100%;
}

.academy-public-event-card__hero-img-wrap ::v-deep img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.title-line {
  border-bottom: 1px solid var(--v-plusIndigoLighten5-base, #E8EAF6);

  .theme--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }
}
</style>
